import React, { useEffect, useState, useCallback, Dispatch } from 'react';
import { Dropdown } from 'components/reusable/Dropdown';
import { addRoute, changeRoute, getRoutes, deleteRoute } from 'data/routeData';
import { EditRoutes } from './EditRoutes';
import { RouteType } from 'types/map';
import { objectIsEqual } from 'utils/helpers';
import { getPathData } from 'data/navigation';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { add, change, remove, replace } from 'hooks/routesSlice';

const EDIT_ID = 'NEW';

type Props = {
  styles: any;
  displayError: Dispatch<any>;
  confirmAction: Dispatch<any>;
};

export function Routes({ styles, displayError, confirmAction }: Props) {
  const dispatch = useAppDispatch();
  const routes = useAppSelector((state) => state.routes);
  const edit = useAppSelector((state) => state.edit);
  const [originalRoutes, setOriginalRoutes] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const updateRoutes = useCallback(async () => {
    console.log('----------- GETTING ROUTES DATA -----------');
    getRoutes().then((data) => {
      setOriginalRoutes(data);
      dispatch(replace({ routes: data }));
    });
  }, [dispatch]);

  const addNewRoute = useCallback(() => {
    const newRoute = {
      id: EDIT_ID,
      name: '',
      routes: undefined,
      distance: 0,
      duration: { hours: 0, minutes: 0 },
      segments: [],
    };

    dispatch(add({ route: newRoute }));
  }, [dispatch]);

  const cancelChanges = useCallback(
    (route: RouteType) => {
      if (route.id === EDIT_ID) {
        dispatch(remove({ id: route.id }));
      } else {
        const original = originalRoutes.find(
          (r: RouteType) => r.id === route.id,
        );
        dispatch(change({ route: original }));
      }
    },
    [originalRoutes, dispatch],
  );

  const saveRoute = useCallback(
    (route: RouteType) => {
      if (route.id === EDIT_ID) {
        return addRoute(route).then(updateRoutes);
      }

      const original = originalRoutes.find((r) => r.id === route.id);
      if (route.routes !== original.routes) {
        const { routes: currentRoutes, id, name, color } = route;
        return getPathData({ routes: currentRoutes, id, name, color }).then(
          (data) => {
            changeRoute({
              ...route,
              routes: data.routes,
            }).then(updateRoutes);
          },
        );
      }

      return changeRoute(route).then(updateRoutes);
    },
    [updateRoutes, originalRoutes],
  );

  const hasChanged = useCallback(
    (route: RouteType) => {
      if (originalRoutes) {
        const original = originalRoutes.find(
          (o: RouteType) => o.id === route.id,
        );
        return !objectIsEqual(original, route);
      }
      return false;
    },
    [originalRoutes],
  );

  const actualDeleteRoute = useCallback(
    (id: string) => {
      if (id === EDIT_ID) {
        dispatch(remove({ id }));
        return Promise.resolve();
      }

      return deleteRoute(id)
        .then(updateRoutes)
        .catch(() =>
          displayError('An error has occurred please try again later.'),
        );
    },
    [updateRoutes, displayError, dispatch],
  );

  const confirmDelete = useCallback(
    (route: RouteType) => {
      confirmAction({
        title: 'Delete Route',
        name: route.name,
        delete: () => actualDeleteRoute(route.id),
      });
    },
    [confirmAction, actualDeleteRoute],
  );

  useEffect(() => {
    updateRoutes();
  }, [updateRoutes]);

  useEffect(() => {
    if (!edit.value.coords?.length || !routes) return;
    const index = routes.value.findIndex((r: RouteType) => r.id === EDIT_ID);
    if (index > -1) {
      if (routes.value[index].routes !== edit.value.coords) {
        dispatch(
          change({
            route: {
              ...routes.value[index],
              routes: edit.value.coords,
            },
          }),
        );
      }
    }
  }, [edit, routes, dispatch]);

  return (
    <Dropdown
      title={'Routes'}
      isCollapsed={isCollapsed}
      setCollapsed={() => setIsCollapsed(!isCollapsed)}
    >
      <EditRoutes
        styles={styles}
        hasChanged={hasChanged}
        addRoute={addNewRoute}
        deleteRoute={confirmDelete}
        saveRoute={saveRoute}
        cancelChanges={cancelChanges}
        displayError={displayError}
      />
    </Dropdown>
  );
}
